import {useNavigate, useParams} from "react-router-dom";
import {skipToken} from "@reduxjs/toolkit/query";
import {
    useCreateVendorPricingMutation,
    useGetVendorPricingByIdQuery,
    useUpdateVendorPricingMutation
} from "./vendorPricingApi";
import Breadcrumb, {Crumb} from "../../../components/common/Breadcrumbs";
import AuditableInfoButton from "../../../components/common/form/AuditableInfoButton";
import React, {ChangeEvent, FC, FormEvent, useEffect, useState} from "react";
import Form from "../../../components/common/form/Form";
import TextAreaField from "../../../components/common/form/TextAreaField";
import {hasPermission, jwtDecode} from "../../../helpers/utils/AccessControlUtils";
import SelectField from "../../../components/common/form/SelectField";
import {createOptionListForSelectTag, isNullOrEmpty} from "../../../helpers/utils/Utils";
import {useGetEnumsQuery} from "../../../app/globalApi";
import InputField from "../../../components/common/form/InputField";
import {useGetAllStatesQuery} from "../../data/states/statesApi";
import {useGetAllCountyByStateAbbrQuery} from "../../data/counties/countiesApi";
import {useSearchMetroAreasQuery} from "../../data/metroAreas/metroAreasApi";
import {handleRTKQuery} from "../../../helpers/utils/RtkQueryUtils";
import {Routes as AppRoutes} from "../../../routes";
import {getEnumDisplayNameByConstant} from "../../../helpers/utils/EnumUtils";
import {useGetAllTenantsQuery} from "../../customer/tenantApi";
import ButtonTray from "../../../components/common/form/ButtonTray";

interface props {
    crumbs: Crumb[],
    title: string,
    btnText: string,
    isEditable?: boolean
}

const VendorPricingForm: FC<props> = ({crumbs, title, btnText, isEditable = true}) => {

    const navigate = useNavigate();
    let {vendorPricingId} = useParams();

    const [createVendor, createVendorApiResponse] = useCreateVendorPricingMutation();
    const [updateVendor, updateVendorApiResponse] = useUpdateVendorPricingMutation();
    const vendorByIdApi = useGetVendorPricingByIdQuery(vendorPricingId ?? skipToken);
    const [pricingData, setPricingData] = useState<any>({});
    const stateApiResponse = useGetAllStatesQuery("");
    const tenantApiResponse = useGetAllTenantsQuery("");
    const {data: enums} = useGetEnumsQuery("");
    const {
        data: countyByStateAbbr,
        isLoading: countyByStateAbbrLoading,
        isFetching: countyByStateAbbrFetching
    } = useGetAllCountyByStateAbbrQuery(
        stateApiResponse?.data?.find((el: any) => el.id === pricingData?.state?.value)?.stateAbbreviation ?? skipToken
    );

    const {
        data: metroAreaByStateAbbr,
        isLoading: metroAreaByStateAbbrLoading,
        isFetching: metroAreaByStateAbbrFetching
    } = useSearchMetroAreasQuery({
        pageSize: 10000,
        stateName: stateApiResponse?.data?.find((el: any) => el.id === pricingData?.state?.value)?.stateName
    }, {
        skip: isNullOrEmpty(pricingData?.state?.value) || !stateApiResponse?.data // Skip if no stateId or statesApiResult data is available
    });

    const handleOnChange = (e: ChangeEvent<HTMLInputElement> | {
        name: string;
        value: any
    } | ChangeEvent<HTMLTextAreaElement>) => {
        const {name, value} = "target" in e ? e.target : e;

        setPricingData((prevData: any) => {
            let updatedData = {...prevData, [name]: value};

            // Clear county and state if state is cleared
            if (name === "state") {
                updatedData = {...updatedData, county: null, metroArea: null};
            }

            return updatedData;
        });
    }

    useEffect(() => {
        let obj = vendorByIdApi?.data;
        if (obj) {
            setPricingData(
                {
                    ...obj,
                    state: obj?.state ? {
                        value: obj?.state?.id,
                        label: obj?.state?.stateName
                    } : null,
                    county: obj?.county ? {
                        value: obj?.county?.id,
                        label: obj?.county?.name
                    } : null,
                    metroArea: obj?.metroArea ? {
                        value: obj?.metroArea?.id,
                        label: obj?.metroArea?.name
                    } : null,
                    tenantId: jwtDecode()?.customerLevel === "SYS" ? {
                        label: tenantApiResponse?.data?.filter((el: any) => el.id === obj?.tenantId)[0]?.name,
                        value: obj?.tenantId,
                    } : jwtDecode()?.vendor,
                    productType: obj?.productType ? {
                        value: obj?.productType,
                        label: getEnumDisplayNameByConstant(enums?.["ProductType"], obj?.productType)
                    } : null,
                    vendor: obj?.vendor ? {
                        value: obj?.vendor,
                        label: getEnumDisplayNameByConstant(enums?.["VendorEnum"], obj?.vendor)
                    } : null,
                }
            )
        }

    }, [vendorByIdApi?.data, tenantApiResponse?.data, enums]);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const commonObj = {
            ...pricingData,
            productType: pricingData?.productType?.value,
            vendor: pricingData?.vendor?.value,
            state: pricingData?.state?.value ? stateApiResponse?.data.filter((el: any) => el.id === pricingData?.state?.value)[0] : null,
            county: pricingData?.county?.value ? countyByStateAbbr?.filter((el: any) => el.id === pricingData?.county?.value)[0] : null,
            metroArea: pricingData?.metroArea?.value ? metroAreaByStateAbbr.filter((el: any) => el.id === pricingData?.metroArea?.value)[0] : null,
            tenantId: jwtDecode()?.customerLevel === "SYS" ? pricingData?.tenantId?.value : jwtDecode()?.tenantId
        };

        const createOrUpdateUser = async () => {
            if (btnText === "Create") {
                await createVendor(commonObj).unwrap();
            } else {
                await updateVendor({
                    id: vendorPricingId,
                    requestBody: commonObj,
                }).unwrap();
            }
        };

        await handleRTKQuery(createOrUpdateUser, () => {
            setPricingData({});
            navigate(AppRoutes.VendorPricing.path);
        });
    }

    return (
        <>
            <div className="px-4 py-2">

                <div className={"justify-between sm:block md:flex lg:flex"}>
                    <Breadcrumb crumbs={crumbs}/>
                    { hasPermission( ["AUDIT-V"]) &&
                        <AuditableInfoButton obj={vendorByIdApi?.data}/>
                    }
                </div>

                <div className="flex justify-center mt-3">
                    <div className="bg-surface-2 lg:w-4/6 sm:w-full p-5 rounded-md">
                        <h3 className="text-xl font-semibold text-text-0 pb-3 border-b border-surface-3">{title}</h3>
                        <Form onSubmit={handleSubmit} fields={<>
                            <div className="grid grid-cols-12">

                                <TextAreaField
                                    name="description"
                                    label="Description"
                                    onChange={handleOnChange}
                                    value={pricingData.description || ""}
                                    placeholder="Enter Description..."
                                    rows={2}
                                />

                                <SelectField
                                    placeholder='Select Product Type...'
                                    value={pricingData?.productType || null}
                                    options={createOptionListForSelectTag(enums?.["ProductType"], "value", "constant")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({name: "productType", value: selectedOption})
                                    }}
                                    label={"Product Type"}
                                    required={true}
                                />

                                <SelectField
                                    placeholder='Select Vendor...'
                                    value={pricingData?.vendor || null}
                                    options={createOptionListForSelectTag(enums?.["VendorEnum"], "value", "constant")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({name: "vendor", value: selectedOption})
                                    }}
                                    label={"Vendor"}
                                    required={true}
                                />

                                {jwtDecode()?.customerLevel === "SYS" && <SelectField
                                    placeholder='Select Tenant...'
                                    value={pricingData?.tenantId || null}
                                    options={createOptionListForSelectTag(tenantApiResponse?.data, "name", "id")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({name: "tenantId", value: selectedOption})
                                    }}
                                    isLoading={tenantApiResponse.isLoading || tenantApiResponse.isFetching}
                                    label={"Tenant"}
                                />}


                                <SelectField
                                    placeholder='Select State...'
                                    value={pricingData?.state || null}
                                    options={createOptionListForSelectTag(stateApiResponse?.data, "stateName", "id")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({name: "state", value: selectedOption})
                                    }}
                                    isLoading={stateApiResponse.isLoading || stateApiResponse.isFetching}
                                    label={"State"}
                                />

                                <SelectField
                                    label="County"
                                    value={pricingData?.county}
                                    placeholder="Select County..."
                                    options={createOptionListForSelectTag(countyByStateAbbr, "name", "id")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({name: "county", value: selectedOption})
                                    }}
                                    isLoading={countyByStateAbbrLoading || countyByStateAbbrFetching}
                                    isDisabled={pricingData?.metroArea || !pricingData?.state}
                                />

                                <SelectField
                                    label="Metro Area"
                                    value={pricingData?.metroArea}
                                    placeholder="Select Metro Area..."
                                    options={createOptionListForSelectTag(metroAreaByStateAbbr, "name", "id")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({name: "metroArea", value: selectedOption})
                                    }}
                                    isLoading={metroAreaByStateAbbrLoading || metroAreaByStateAbbrFetching}
                                    isDisabled={pricingData?.county || !pricingData?.state}
                                />

                                <InputField
                                    name={"vendorPrice"}
                                    label="Price"
                                    onChange={handleOnChange}
                                    value={pricingData?.vendorPrice || ""}
                                    type="number"
                                    placeholder="Enter Price..."
                                    required={true}
                                />

                                <InputField
                                    name={"vendorCost"}
                                    label="Cost"
                                    onChange={handleOnChange}
                                    value={pricingData?.vendorCost || ""}
                                    type="number"
                                    placeholder="Enter Cost..."
                                    required={true}
                                />

                            </div>


                            <ButtonTray
                                buttons={[
                                    {
                                        buttonProps: {
                                            btnText: "Cancel",
                                            onClick: () => navigate(AppRoutes.VendorPricing.path),
                                            type: "close",
                                        },
                                        buttonType: "button"
                                    },
                                    {
                                        buttonProps: {
                                            btnText: "Submit",
                                            type: "submit",
                                            isLoading: (createVendorApiResponse.isLoading || updateVendorApiResponse.isLoading),
                                            isVisible: isEditable
                                        },
                                        buttonType: "button"
                                    }
                                ]}
                                align="end"
                                gap={2}
                            />

                        </>}/>
                    </div>
                </div>
            </div>
        </>
    )
}
export default VendorPricingForm;